import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Plus } from 'lucide-react';
import { Pagination } from '../../components/shared/Pagination';
import { useUniversities } from './hooks/useUniversities';
import {
  UniversitiesTable,
  UniversityFilters,
  UniversityDialog,
  DirectionsDialog,
} from './components';
import { UniversitiesPageProps } from './types';

export default function UniversitiesPage({ isDark }: UniversitiesPageProps) {
  const {
    universities,
    isLoading,
    currentPage,
    totalPages,
    total,
    searchQuery,
    regionFilter,
    regions,
    availableYears,
    selectedYear,
    setSelectedYear,
    isCreateDialogOpen,
    isEditDialogOpen,
    formData,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    setSearchQuery,
    setRegionFilter,
    setCurrentPage,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    isDirectionsDialogOpen,
    selectedUniversity,
    directionsData,
    isDirectionsLoading,
    directionsForm,
    directionsLanguage,
    setDirectionsForm,
    setDirectionsLanguage,
    handleViewDirections,
    setIsDirectionsDialogOpen,
  } = useUniversities();

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn(
            'text-2xl lg:text-3xl font-bold tracking-tight',
            isDark ? 'text-white' : 'text-[#0F172A]'
          )}>Universities</h1>
          <p className={cn(
            'text-sm mt-1',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Manage universities and admission directions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add University
          </Button>
        </div>
      </div>

      {/* Table Card */}
      <Card className={cn(
        'rounded-2xl border-0 overflow-hidden transition-all duration-300',
        isDark
          ? 'bg-[#1E293B] shadow-lg shadow-black/10'
          : 'bg-white shadow-lg shadow-slate-200/50'
      )}>
        <UniversityFilters
          isDark={isDark}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          regionFilter={regionFilter}
          onRegionChange={setRegionFilter}
          regions={regions}
          availableYears={availableYears}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
        />
        <UniversitiesTable
          isDark={isDark}
          universities={universities}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewDirections={handleViewDirections}
        />
        {totalPages > 1 && (
          <Pagination
            isDark={isDark}
            currentPage={currentPage}
            totalPages={totalPages}
            total={total}
            itemsPerPage={20}
            onPageChange={setCurrentPage}
          />
        )}
      </Card>

      {/* Create Dialog */}
      <UniversityDialog
        isDark={isDark}
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        title="Add University"
        description="Add a new university to the system"
        submitLabel="Create"
      />

      {/* Edit Dialog */}
      <UniversityDialog
        isDark={isDark}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        title="Edit University"
        description="Update university information"
        submitLabel="Save Changes"
      />

      {/* Directions Dialog */}
      <DirectionsDialog
        isDark={isDark}
        isOpen={isDirectionsDialogOpen}
        onOpenChange={setIsDirectionsDialogOpen}
        university={selectedUniversity}
        directionsData={directionsData}
        isLoading={isDirectionsLoading}
        form={directionsForm}
        language={directionsLanguage}
        onFormChange={setDirectionsForm}
        onLanguageChange={setDirectionsLanguage}
      />
    </div>
  );
}
